/**
 * Situational talk (คำพูดตามสถานการณ์): the short lines townsfolk and key NPCs say in a speech bubble,
 * matched to the hour, the weather, the place, the player (school, karma, PK, wounds, rank, a duel
 * just won), what has just happened round them, the rumours of the quest under way, and how they
 * regard the player (systems/npcMemory.ts). Each person speaks in the voice of a role.
 *
 * A group of lines says who may say it (`who`), when (`on`: a greeting, an aside, thanks for a gift)
 * and on what condition (`when`); systems/npcTalk.ts picks among the groups that match, the more
 * specific the likelier, then one line of the group. `act` asks for a gesture with the line
 * (an act of data/npcActs.ts, played for a moment with the line).
 * `{name}` is the player's name, `{school}` the player's school.
 *
 * Data only. Add lines and groups here; the rules are in systems/npcTalk.ts.
 */
import type { Karma, PkColor } from '../karma';
import type { DayPart } from './folk';
import type { NpcActId, NpcRole } from './npcActs';
import type { WeatherKind } from './weather';
import { START, type ZoneId } from './zones';

/** The voice a person speaks in. */
export type TalkRole =
  | 'elder'
  | 'vendor'
  | 'innkeeper'
  | 'farmer'
  | 'fisher'
  | 'guard'
  | 'child'
  | 'martial'
  | 'lady'
  | 'monk'
  | 'scholar'
  | 'guide'
  | 'master';

export const TALK_ROLES: readonly TalkRole[] = [
  'elder',
  'vendor',
  'innkeeper',
  'farmer',
  'fisher',
  'guard',
  'child',
  'martial',
  'lady',
  'monk',
  'scholar',
  'guide',
  'master',
];

/** How a person regards the player (systems/npcMemory.ts `standingTier`). */
export type Standing = 'hated' | 'wary' | 'neutral' | 'friendly' | 'honoured';

/**
 * A gesture asked for with a line: an act of data/npcActs.ts, set for a moment through its
 * `setAct` hook. `bow`, `wave` and `cower` are also the townsfolk's own greetings (FolkBrain).
 */
export type TalkAct = Extract<
  NpcActId,
  'bow' | 'wave' | 'cower' | 'baoquan' | 'laugh' | 'sigh' | 'cheer' | 'argue'
>;

/** What the line is: said to a passer-by who comes close, said aloud now and then, or thanks for a gift. */
export type TalkOn = 'greet' | 'idle' | 'gift';

/** Something that has just happened, remembered for a while (systems/npcTalk.ts `RECENT_FOR`). */
export type TalkRecent =
  /** The player won a bout (arena or ladder). */
  | 'wonDuel'
  | 'lostDuel'
  /** The player handed in a quest. */
  | 'questDone'
  /** The player killed a monster near this person. */
  | 'saved'
  /** The player struck another player near here. */
  | 'pkNear'
  /** The world boss is abroad. */
  | 'boss'
  /** The world boss has just fallen. */
  | 'bossDown';

/** Conditions on a group of lines; every one given must hold. */
export interface TalkWhen {
  /** Named times of day (data/time.ts `TIME_PERIODS` ids). */
  readonly period?: readonly string[];
  readonly part?: readonly DayPart[];
  readonly weather?: readonly WeatherKind[];
  readonly zone?: readonly ZoneId[];
  /** The landmark of the speaker's station (inn, plaza, pavilion, fields…). */
  readonly at?: readonly string[];
  readonly standing?: readonly Standing[];
  /** In a school (true), in none (false), or in one of these. */
  readonly school?: boolean | readonly string[];
  readonly karma?: readonly Karma[];
  readonly pk?: readonly PkColor[];
  /** The player below 40% of their health. */
  readonly hurt?: boolean;
  /** The player's realm (cultivation.ts `REALMS` index), from and to inclusive. */
  readonly realm?: readonly [number, number];
  readonly recent?: readonly TalkRecent[];
  /** The quest under way (data/quests.ts ids). */
  readonly quest?: readonly string[];
}

export interface TalkGroup {
  readonly who: readonly TalkRole[] | 'all';
  readonly on: readonly TalkOn[];
  readonly when?: TalkWhen;
  readonly act?: TalkAct;
  /** Weight of the group among those that match (1 if left out). */
  readonly w?: number;
  readonly lines: readonly string[];
}

/** The shop's note on a price changed by the standing (systems/npcMemory.ts `PRICE_MULT`). */
export const PRICE_HINT: Readonly<Record<Standing, string | null>> = {
  honoured: 'ราคาผู้มีพระคุณ −10%',
  friendly: 'ราคามิตรภาพ −5%',
  neutral: null,
  wary: 'ราคาคนแปลกหน้า +10%',
  hated: 'ราคาคนที่ไม่ต้อนรับ +25%',
};

/** Which voice each townsperson speaks in (data/folk.ts ids). */
export const FOLK_ROLE: Readonly<Record<string, TalkRole>> = {
  hai: 'fisher',
  yu: 'child',
  liu: 'innkeeper',
  niu: 'farmer',
  sun: 'farmer',
  zheng: 'guard',
  skyblade: 'martial',
  moonwind: 'martial',
  mist: 'lady',
  mei: 'vendor',
  bao: 'child',
  lao: 'guard',
  // จู๋หมิง's facility folk (data/zhumingFolk.ts)
  'zm-stable': 'guide',
  'zm-smith': 'vendor',
  'zm-tailor': 'vendor',
  'zm-carpenter': 'farmer',
  'zm-farmer': 'farmer',
  'zm-woodcutter': 'farmer',
  'zm-beggar': 'fisher',
  'zm-musician': 'lady',
  'zm-fishing': 'fisher',
  'zm-inn': 'innkeeper',
  'zm-yard': 'martial',
};

/** The key NPCs' voices (data/folk.ts `NPC_LINES` roles); a school master by the school. */
export const KEY_ROLE: Readonly<Record<string, TalkRole>> = {
  elder: 'elder',
  merchant: 'vendor',
  guide: 'guide',
  master: 'master',
};
/** The voice of an act person (systems/npcActs.ts `actRoleOf`), by their act role (data/npcActs.ts). */
export const ACT_VOICE: Readonly<Record<NpcRole, TalkRole>> = {
  villager: 'farmer',
  vendor: 'vendor',
  guard: 'guard',
  monk: 'monk',
  child: 'child',
  elder: 'elder',
  fisher: 'fisher',
  smith: 'vendor',
  scholar: 'scholar',
  performer: 'scholar',
  disciple: 'martial',
  farmer: 'farmer',
  innkeeper: 'innkeeper',
};
export const MASTER_ROLE: Readonly<Record<string, TalkRole>> = { 'bell-stone': 'monk', 'pine-ink': 'scholar' };

const GREET: readonly TalkOn[] = ['greet'];
const IDLE: readonly TalkOn[] = ['idle'];
const ANY: readonly TalkOn[] = ['greet', 'idle'];
const GIFT: readonly TalkOn[] = ['gift'];

/** Townsfolk who keep a trade. */
const TRADE: readonly TalkRole[] = ['vendor', 'innkeeper', 'farmer', 'fisher'];
/** People of the martial world. */
const WULIN: readonly TalkRole[] = ['martial', 'master', 'guide'];

export const TALK: readonly TalkGroup[] = [
  // ───────────────────────── greetings by voice ─────────────────────────
  {
    who: ['elder'],
    on: GREET,
    lines: [
      'มาแล้วหรือ {name} นั่งพักก่อนเถิด',
      'คนหนุ่มเดินไวนัก ข้าตามไม่ทันแล้ว',
      'ทางที่เจ้าเดิน ข้าเคยเดินมาก่อน ระวังหลุมที่ข้าตกด้วย',
      'ยุทธภพกว้างนัก แต่คนดีหายากกว่าทอง',
      'ใจนิ่งเสียก่อน กระบี่จึงจะนิ่ง',
    ],
  },
  {
    who: ['vendor'],
    on: GREET,
    act: 'wave',
    lines: [
      'เชิญๆ ท่านจอมยุทธ์ แวะดูก่อน ไม่ซื้อไม่ว่า',
      'ของใหม่เพิ่งมาถึงเช้านี้ ท่านเป็นคนแรกที่ได้เห็น',
      'ตำลึงเดียวก็ซื้อได้ ตำลึงเดียวก็ขายได้',
      'ท่านดูท่าทางเป็นคนมีวาสนา ของดีต้องคู่คนดี',
      'เดินทางไกลมาหรือ ข้ามีเสบียงแห้งให้ติดตัวไป',
    ],
  },
  {
    who: ['innkeeper'],
    on: GREET,
    act: 'bow',
    lines: [
      'เชิญด้านใน ชาร้อนพร้อม ซาลาเปาเพิ่งออกจากลังถึง',
      'ท่านจะพักหรือจะกิน ห้องบนยังว่างหนึ่งห้อง',
      'สุราหมักสามปี ดื่มแล้วลืมทางกลับบ้านเลยทีเดียว',
      'โรงเตี๊ยมข้าเล็ก แต่ไม่เคยไล่แขกคนไหนออกไปหิวโหย',
    ],
  },
  {
    who: ['farmer'],
    on: GREET,
    lines: [
      'สวัสดีขอรับ ระวังเหยียบคันนาด้วยนะ',
      'จอมยุทธ์มาเดินถึงนา หายากนัก',
      'ข้าวปีนี้รวงงาม ถ้าฟ้าไม่แกล้งเสียก่อน',
      'ท่านกินข้าวมาหรือยัง ข้ามีข้าวห่อใบบัว',
    ],
  },
  {
    who: ['fisher'],
    on: GREET,
    lines: [
      'เบาๆ หน่อย ปลาตื่นหมดพอดี',
      'ทะเลวันนี้ใจดี ท่านโชคดีที่มาวันนี้',
      'เดินเลียบหาดได้ แต่อย่าเดินลงไปในน้ำลึกนะ',
      'ลมทะเลพัดมาทั้งปลาทั้งข่าว ท่านอยากฟังอันไหน',
    ],
  },
  {
    who: ['guard'],
    on: GREET,
    act: 'baoquan',
    lines: [
      'หยุดก่อน… อ้อ ท่านนั่นเอง ผ่านไปได้',
      'ในเขตเมือง ห้ามชักอาวุธ ห้ามก่อเรื่อง',
      'เดินทางสะดวกดีหรือ ถนนช่วงนี้ไม่ค่อยเรียบร้อย',
      'มีเรื่องอะไรผิดสังเกต บอกข้าได้ทุกเมื่อ',
    ],
  },
  {
    who: ['child'],
    on: GREET,
    act: 'wave',
    lines: [
      'พี่จอมยุทธ์! ให้ข้าดูกระบี่หน่อยได้ไหม',
      'พี่เหาะได้จริงหรือ เหาะให้ดูหน่อย!',
      'แม่บอกว่าห้ามคุยกับคนแปลกหน้า… แต่พี่ไม่แปลกหน้าแล้ว',
      'ข้าจะเป็นจอมยุทธ์อย่างพี่ให้ได้!',
      'วิ่งแข่งกันไหม ข้าไม่แพ้ใครในหมู่บ้านนะ',
    ],
  },
  {
    who: ['martial'],
    on: GREET,
    act: 'baoquan',
    lines: [
      'สหายร่วมยุทธภพ ขอคารวะ',
      'ฝีเท้าท่านเบานัก ฝึกวิชาตัวเบามานานแล้วกระมัง',
      'วันหน้าหากมีโอกาส ขอประลองฝีมือสักกระบวน',
      'กระบี่ในมือท่าน ดูแล้วยังไม่เคยลิ้มรสความพ่ายแพ้',
    ],
  },
  {
    who: ['lady'],
    on: GREET,
    act: 'bow',
    lines: [
      'คุณชายเดินทางมาไกล คงเหนื่อยไม่น้อย',
      'ลมวันนี้หอมดอกไม้จากหุบเขา ท่านได้กลิ่นหรือไม่',
      'ข้าน้อยคารวะ',
      'บุปผาบานแล้วร่วง คนก็พบแล้วจาก ช่างเป็นธรรมดา',
    ],
  },
  {
    who: ['monk'],
    on: GREET,
    act: 'bow',
    lines: [
      'อมิตาภพุทธ ประสกเดินทางปลอดภัยดีหรือ',
      'วางกระบี่ได้ ใจจึงจะสงบ',
      'ทุกก้าวคือการฝึกตน ประสกอย่าได้รีบร้อน',
      'บาปบุญติดตัวไปทุกที่ เหมือนเงาตามร่าง',
    ],
  },
  {
    who: ['scholar'],
    on: GREET,
    act: 'bow',
    lines: [
      'ท่านมาพอดี ข้ากำลังหาคนถกเรื่องตำราอยู่',
      'อ่านหนังสือหมื่นเล่ม ไม่สู้เดินทางหมื่นลี้',
      'วิชาบู๊กับวิชาบุ๋น สุดท้ายก็คือวิชาคนเหมือนกัน',
      'คารวะ ท่านจอมยุทธ์ ข้าน้อยเป็นเพียงบัณฑิตยากจน',
    ],
  },
  {
    who: ['guide'],
    on: GREET,
    lines: [
      'จะไปทางไหน บอกข้ามา ทางลัดข้ารู้ทุกสาย',
      'ข้ามเขาลูกนั้นต้องใช้วิชาตัวเบา หรือไม่ก็ขาแข็งๆ',
      'ถนนใหญ่ปลอดภัยกว่า แต่ทางเล็กไปถึงก่อน',
    ],
  },
  {
    who: ['master'],
    on: GREET,
    lines: [
      'ฝึกฝนวันละนิด ดีกว่าฝึกหนักแล้วเลิกกลางทาง',
      'ข้าดูท่ายืนของเจ้าก็รู้ว่าฝึกมาแค่ไหน',
      'วิชาไม่เลือกคน แต่คนต้องเลือกฝึก',
    ],
  },

  // ───────────────────────── the hour ─────────────────────────
  {
    who: 'all',
    on: GREET,
    when: { period: ['daybreak', 'early'] },
    lines: [
      'อรุณสวัสดิ์ ออกเดินทางแต่เช้าเชียว',
      'ตื่นเช้ากว่าไก่อีกหรือนี่',
      'หมอกเช้ายังไม่จาง ระวังทางลื่น',
    ],
  },
  {
    who: TRADE,
    on: IDLE,
    when: { period: ['daybreak', 'early'] },
    lines: [
      'เช้าแล้ว รีบทำงานก่อนแดดแรง',
      'ไก่ขันสามรอบแล้ว ยังไม่ตื่นกันอีก',
      'ข้าวต้มร้อนๆ สักชามก่อนออกนา',
      'ตลาดเช้าคนแน่นทุกวันตั้งแต่ศิลาตื่น',
    ],
  },
  {
    who: 'all',
    on: ANY,
    when: { period: ['noon'] },
    lines: [
      'เที่ยงแล้ว แดดแรงนัก หาร่มนั่งก่อนเถิด',
      'ท้องร้องแล้ว ได้เวลากินข้าว',
      'เงาหดอยู่ใต้เท้า เที่ยงตรงพอดี',
    ],
  },
  {
    who: TRADE,
    on: IDLE,
    when: { period: ['afternoon'] },
    lines: ['บ่ายนี้ง่วงนัก ชาสักป้านก็คงดี', 'ลมบ่ายพัดเย็นสบาย', 'อีกไม่นานก็เลิกงานแล้ว'],
  },
  {
    who: 'all',
    on: ANY,
    when: { period: ['evening', 'dusk'] },
    lines: [
      'ตะวันจะตกแล้ว รีบกลับก่อนมืด',
      'ควันไฟหุงข้าวขึ้นทุกหลังคาแล้ว',
      'พลบค่ำทีไร ข้าก็คิดถึงบ้านเกิดทุกที',
    ],
  },
  {
    who: ['innkeeper'],
    on: ANY,
    when: { part: ['dusk', 'night'] },
    lines: [
      'จุดโคมแล้ว แขกจะเข้ามากันละ',
      'คืนนี้มีน้ำแกงกระดูกหมู เคี่ยวมาตั้งแต่บ่าย',
      'ห้องเต็มเกือบหมดแล้ว ท่านจะพักต้องรีบหน่อย',
    ],
  },
  {
    who: 'all',
    on: GREET,
    when: { part: ['night'] },
    lines: [
      'ดึกป่านนี้ยังไม่นอนอีกหรือ',
      'กลางคืนข้างนอกหมู่บ้านไม่ใช่ที่เดินเล่น',
      'ระวังตัวด้วย ดึกแล้วอะไรก็เกิดขึ้นได้',
    ],
  },
  {
    who: ['guard'],
    on: IDLE,
    when: { part: ['night'] },
    lines: ['ยามสอง ปิดประตูลงกลอน ระวังโจร~', 'ระวังฟืนไฟ~ ระวังคนแปลกหน้า~', 'เงียบนัก… เงียบเกินไป'],
  },
  {
    who: ['child'],
    on: ANY,
    when: { part: ['night'] },
    lines: [
      'แม่เรียกเข้าบ้านแล้ว… ขออีกเดี๋ยวเดียว',
      'กลางคืนผีออกจริงหรือพี่',
      'ข้าไม่กลัวมืดหรอก… จริงๆ นะ',
    ],
  },
  {
    who: ['monk', 'scholar', 'elder'],
    on: IDLE,
    when: { period: ['deep', 'predawn'] },
    lines: ['ใจสงบเหมือนน้ำนิ่งยามดึก', 'ดาวเหนือยังอยู่ที่เดิม คนเท่านั้นที่เปลี่ยน'],
  },

  // ───────────────────────── the weather ─────────────────────────
  {
    who: 'all',
    on: ANY,
    when: { weather: ['rain'] },
    lines: ['ฝนมาอีกแล้ว เข้ามาหลบก่อนเถิด', 'ฝนแบบนี้ ทางขึ้นเขาลื่นแน่', 'อย่าลืมกางร่ม เดี๋ยวจะไม่สบาย'],
  },
  {
    who: ['farmer'],
    on: ANY,
    when: { weather: ['rain'] },
    act: 'laugh',
    lines: ['ฝนมาแล้ว! ข้าวในนาได้ดื่มน้ำเสียที', 'ฟ้าเมตตา ปีนี้ไม่ต้องหาบน้ำขึ้นนา'],
  },
  {
    who: ['vendor'],
    on: IDLE,
    when: { weather: ['rain', 'storm'] },
    act: 'sigh',
    lines: ['ฝนตกแบบนี้ ใครจะมาซื้อของ', 'ผ้าคลุมแผงหน่อย ของเปียกหมดแล้ว'],
  },
  {
    who: 'all',
    on: ANY,
    when: { weather: ['storm'] },
    act: 'cower',
    lines: [
      'ฟ้าผ่าเปรี้ยง! ข้าตกใจแทบสิ้นสติ',
      'พายุแรงนัก ท่านอย่าขึ้นที่สูงเชียว',
      'ฟ้าคำรามแบบนี้ เหมือนมีเทพบนฟ้าประลองกัน',
    ],
  },
  {
    who: ['fisher'],
    on: ANY,
    when: { weather: ['storm'] },
    lines: ['คลื่นสูงแบบนี้ ไม่มีใครกล้าออกเรือ', 'ผูกเรือให้แน่น ทะเลกำลังคลั่ง'],
  },
  {
    who: 'all',
    on: ANY,
    when: { weather: ['fog'] },
    lines: ['หมอกหนาจนมองไม่เห็นมือตัวเอง', 'ในหมอกแบบนี้ โจรชอบนักแล', 'เดินช้าๆ หมอกหนาอาจหลงทางได้'],
  },
  {
    who: 'all',
    on: ANY,
    when: { weather: ['snow'] },
    lines: ['หิมะตกแล้ว หนาวถึงกระดูก', 'สุราอุ่นสักจอกคงดี หนาวขนาดนี้', 'ทางขึ้นเขาคงปิดหมดแล้ว'],
  },
  {
    who: ['child'],
    on: ANY,
    when: { weather: ['snow'] },
    act: 'laugh',
    lines: ['หิมะ! มาปั้นตุ๊กตาหิมะกัน!', 'ปาหิมะใส่ข้าไม่ได้นะ ข้าหลบได้!'],
  },
  {
    who: 'all',
    on: IDLE,
    when: { weather: ['clear'], part: ['day'] },
    lines: ['ฟ้าใส อากาศดี วันนี้ทำอะไรก็ราบรื่น', 'แดดดีแบบนี้ ตากผ้าได้ทั้งหมู่บ้าน'],
  },
  {
    who: ['fisher'],
    on: IDLE,
    when: { weather: ['clear', 'cloudy'], part: ['dawn'] },
    lines: ['น้ำขึ้นเช้านี้ ปลากระบอกเข้าฝั่งแน่', 'ทะเลเรียบเหมือนกระจก ฟ้าจะใจดีทั้งวัน'],
  },
  {
    who: 'all',
    on: IDLE,
    when: { weather: ['cloudy'] },
    lines: ['ฟ้าครึ้มแบบนี้ ฝนคงมาก่อนค่ำ', 'เมฆดำลอยต่ำ ปวดเข่าทุกที'],
  },

  // ───────────────────────── the place ─────────────────────────
  {
    who: 'all',
    on: IDLE,
    when: { at: ['plaza'] },
    lines: ['ลานกลางนี้ ใครมาก็ต้องผ่าน', 'วันนี้คนพลุกพล่านเหลือเกิน'],
  },
  {
    who: 'all',
    on: IDLE,
    when: { zone: ['plains'] },
    lines: [
      'ศิลาเก้ากระบี่ยืนอยู่กลางลานมาพันปี ไม่เคยเอียงสักนิด',
      'คนจากหกสำนักมาชุมนุมที่ลานศิลา คึกคักไม่หยุด',
      'ว่ากันว่ากลางดึก ศิลาส่งเสียงหึ่งๆ เหมือนกระบี่ในฝัก',
    ],
  },
  {
    who: 'all',
    on: IDLE,
    when: { zone: [START] },
    lines: [
      'หยานหยูเป็นหมู่บ้านเล็ก แต่ข่าวไปไวกว่าม้า',
      'กลิ่นเกลือกับกลิ่นข้าวใหม่ นี่แหละหยานหยู',
      'เรือข้ามฟากพาคนแปลกหน้ามาทุกเที่ยว',
    ],
  },
  {
    who: 'all',
    on: IDLE,
    when: { at: ['inn'] },
    lines: ['กลิ่นซาลาเปาลอยมาอีกแล้ว', 'ในโรงเตี๊ยมมีแต่คนพูดเรื่องศิลาตื่น'],
  },
  {
    who: 'all',
    on: IDLE,
    when: { at: ['fieldsWest', 'fieldsEast'] },
    lines: ['นาแปลงนี้ปู่ของข้าเป็นคนบุกเบิก', 'หมูป่าลงมาขุดคันนาอีกแล้ว'],
  },
  {
    who: 'all',
    on: IDLE,
    when: { at: ['pavilion', 'pagoda'] },
    lines: ['นั่งศาลาแล้วลมพัดเย็นสบาย', 'มองจากตรงนี้ เห็นทิวเขาทอดยาวสุดตา'],
  },

  // ───────────────────────── the player ─────────────────────────
  {
    who: 'all',
    on: GREET,
    when: { school: true },
    lines: [
      'ศิษย์{school}นี่เอง สง่างามจริง',
      'ชุดของ{school} ข้าจำได้แต่ไกล',
      'ได้ยินว่าวิชาของ{school}ลึกล้ำนัก',
    ],
  },
  {
    who: WULIN,
    on: GREET,
    when: { school: true },
    act: 'baoquan',
    lines: [
      'ศิษย์{school} ข้าเคยประลองกับศิษย์พี่ของท่าน ฝีมือไม่ธรรมดา',
      'ฝากคารวะอาจารย์ของท่านด้วย',
      '{school}กับข้า วันหน้าอาจได้ร่วมทางกัน',
    ],
  },
  {
    who: ['martial', 'master'],
    on: GREET,
    when: { school: false },
    lines: [
      'ยังไม่มีสำนักหรือ ยุทธภพไม่เมตตาคนพเนจรนัก',
      'จอมยุทธ์ไร้สังกัด อิสระดีแต่ไม่มีใครคุ้มหัว',
      'ลานศิลามีผู้รับศิษย์ของหกสำนัก ไปดูก่อนเถิด',
    ],
  },
  {
    who: ['master'],
    on: GREET,
    when: { school: ['bell-stone'] },
    act: 'bow',
    lines: ['ศิษย์หินระฆัง จำไว้ว่าหมัดมีไว้คุ้มครอง ไม่ใช่ข่มเหง'],
  },
  {
    who: ['master'],
    on: GREET,
    when: { school: ['mist-peak'] },
    lines: ['ศิษย์ยอดหมอก อ่อนชนะแข็ง ช้าชนะเร็ว อย่าลืม'],
  },
  {
    who: ['master'],
    on: GREET,
    when: { school: ['cinnabar'] },
    lines: ['คนผาดาบชาด… ดาบของพวกเจ้าดุร้ายนัก ระวังมันจะกินเจ้าเสียเอง'],
  },
  {
    who: 'all',
    on: GREET,
    when: { realm: [0, 0] },
    lines: [
      'หน้าใหม่ในยุทธภพหรือ ค่อยๆ ไปทีละก้าว',
      'ยังอ่อนนัก อย่าเพิ่งออกนอกหมู่บ้านไกลเชียว',
      'ทุกยอดฝีมือก็เริ่มจากแขนขาอ่อนแรงทั้งนั้น',
    ],
  },
  {
    who: 'all',
    on: GREET,
    when: { realm: [2, 99] },
    act: 'bow',
    lines: [
      'ไอแห่งยอดฝีมือ… ข้าน้อยคารวะ',
      'ลมรอบตัวท่านนิ่งนัก คงเป็นผู้มีกำลังภายในลึกล้ำ',
      'ชื่อ {name} ข้าได้ยินถึงหูแล้ว',
    ],
  },
  {
    who: ['martial', 'master'],
    on: GREET,
    when: { realm: [2, 99] },
    act: 'baoquan',
    lines: ['ยอดฝีมือมาเยือน ข้าขอชี้แนะสักกระบวนได้หรือไม่', 'ข้าฝึกมาสิบปี ยังไม่ถึงครึ่งของท่าน'],
  },
  {
    who: 'all',
    on: GREET,
    when: { hurt: true },
    lines: [
      'ท่านบาดเจ็บนี่ ไปหาหมอก่อนเถิด',
      'แผลนั่นไม่เบานะ นั่งพักหน่อย',
      'ไปปะทะกับอะไรมา หน้าซีดเชียว',
      'นั่งสมาธิปรับลมปราณก่อน แผลจะหายไว',
    ],
  },
  {
    who: ['innkeeper'],
    on: GREET,
    when: { hurt: true },
    lines: ['บาดเจ็บมาหรือ ข้ามีน้ำแกงโสมตุ๋น กินแล้วฟื้นแรง', 'นอนพักที่นี่สักคืน ข้าไม่คิดค่าห้องแพง'],
  },
  {
    who: ['vendor'],
    on: GREET,
    when: { hurt: true },
    lines: ['ยาสมานแผลไหมท่าน ดูท่าท่านต้องการ', 'แผลแบบนี้ ยาของข้าทาสามวันหาย'],
  },
  {
    who: ['child'],
    on: GREET,
    when: { hurt: true },
    lines: ['พี่เจ็บหรือ… ข้าเป่าให้ไหม', 'พี่ไปสู้กับใครมา ชนะไหม'],
  },
  {
    who: ['monk'],
    on: GREET,
    when: { hurt: true },
    lines: ['บาดแผลกายรักษาง่าย บาดแผลใจรักษายาก ประสกพักเถิด'],
  },

  // Karma and PK: the town knows a killer when it sees one.
  {
    who: 'all',
    on: GREET,
    when: { karma: ['evil', 'mad'] },
    act: 'cower',
    w: 2,
    lines: [
      'คนผู้นี้ไอสังหารแรงนัก…',
      'อย่าไปสบตาเขาเชียว',
      'ข้า… ข้าไม่มีเงินนะท่าน',
      'ได้ยินว่าคนผู้นี้ฆ่าคนไม่กะพริบตา',
    ],
  },
  {
    who: ['guard'],
    on: GREET,
    when: { karma: ['evil', 'mad'] },
    act: 'argue',
    w: 2,
    lines: ['ข้าจับตาดูเจ้าอยู่ ก่อเรื่องเมื่อไรเจ้าได้นอนคุกแน่', 'ในเมืองนี้ยังมีกฎหมาย อย่าลืม'],
  },
  {
    who: ['monk'],
    on: GREET,
    when: { karma: ['evil', 'mad'] },
    act: 'sigh',
    lines: ['กลับตัวยังทัน ประสก วางมีดลงก็เป็นพุทธะได้', 'กรรมที่ก่อไว้ ย่อมย้อนคืนสักวัน'],
  },
  {
    who: 'all',
    on: GREET,
    when: { karma: ['good'] },
    lines: ['ได้ยินว่าท่านปราบคนชั่วไปหลายคน ขอบคุณแทนชาวบ้านด้วย', 'คนดีอย่างท่าน ยุทธภพต้องการอีกมาก'],
  },
  {
    who: 'all',
    on: GREET,
    when: { pk: ['red', 'purple'] },
    act: 'cower',
    w: 3,
    lines: ['หัวแดง! ทุกคนหลบเร็ว!', 'อย่าเข้ามาใกล้! ข้ามีลูกเล็ก!', 'ใครก็ได้ไปตามทหารมาที!'],
  },
  {
    who: ['guard'],
    on: GREET,
    when: { pk: ['red', 'purple'] },
    act: 'argue',
    w: 3,
    lines: ['คนหัวแดง! ยอมให้จับเสียดีๆ', 'ฆ่าคนมามากขนาดนี้ ยังกล้าเดินเข้าเมืองอีก'],
  },
  {
    who: 'all',
    on: IDLE,
    when: { pk: ['orange'] },
    lines: ['ได้ยินว่ามีคนเพิ่งลงมือในเขตนี้', 'มือเปื้อนแล้วล้างยากนะ'],
  },

  // ───────────────────────── what has just happened ─────────────────────────
  {
    who: 'all',
    on: ANY,
    when: { recent: ['wonDuel'] },
    act: 'cheer',
    w: 2,
    lines: [
      'ข้าเห็นท่านประลองเมื่อครู่ ฝีมือสูงส่งนัก!',
      'ชนะสวยงาม คนทั้งลานพูดถึงท่านกันใหญ่',
      'ท่านั้นของท่าน ข้ายังตาลายไม่หาย',
    ],
  },
  {
    who: ['martial', 'master'],
    on: ANY,
    when: { recent: ['wonDuel'] },
    act: 'baoquan',
    w: 2,
    lines: ['ชนะแล้วอย่าเหลิง แพ้แล้วอย่าท้อ', 'ฝีมือดี แต่ข้าเห็นช่องโหว่ตอนเจ้าถอย'],
  },
  {
    who: ['child'],
    on: ANY,
    when: { recent: ['wonDuel'] },
    act: 'laugh',
    w: 2,
    lines: ['พี่ชนะ! พี่ชนะ! สอนข้าท่านั้นหน่อย!', 'ข้าจะเล่าให้ทุกคนฟังว่าพี่ชนะ!'],
  },
  {
    who: 'all',
    on: ANY,
    when: { recent: ['lostDuel'] },
    lines: ['แพ้ครั้งเดียวไม่ได้แปลว่าแพ้ตลอดไป', 'คู่ต่อสู้เก่งนัก ท่านสู้ได้ดีแล้ว'],
  },
  {
    who: 'all',
    on: ANY,
    when: { recent: ['saved'] },
    act: 'bow',
    w: 3,
    lines: [
      'ขอบคุณที่ช่วยไว้! ถ้าไม่มีท่าน ข้าคงไม่รอด',
      'บุญคุณครั้งนี้ ข้าจะไม่ลืม',
      'สัตว์ร้ายนั่นมาถึงหน้าบ้านแล้ว ดีที่ท่านอยู่ตรงนี้',
    ],
  },
  {
    who: ['child'],
    on: ANY,
    when: { recent: ['saved'] },
    act: 'wave',
    w: 3,
    lines: ['พี่เก่งที่สุดเลย! ฟันทีเดียวล้ม!', 'ข้าไม่ได้กลัวนะ… แค่ตกใจนิดหน่อย'],
  },
  {
    who: 'all',
    on: ANY,
    when: { recent: ['questDone'] },
    w: 2,
    lines: ['ได้ยินว่าท่านช่วยผู้เฒ่าทำเรื่องใหญ่สำเร็จ', 'ข่าวดีไปไวนัก ทั้งหมู่บ้านรู้กันหมดแล้ว'],
  },
  {
    who: ['elder'],
    on: ANY,
    when: { recent: ['questDone'] },
    w: 2,
    lines: ['ทำได้ดี {name} ข้าไม่ได้มองคนผิด', 'งานนี้เสร็จ แต่ยุทธภพยังมีอีกหลายงานรอเจ้า'],
  },
  {
    who: 'all',
    on: ANY,
    when: { recent: ['pkNear'] },
    act: 'cower',
    w: 3,
    lines: [
      'อย่าสู้กันตรงนี้! บ้านข้าอยู่ตรงนั้น!',
      'จอมยุทธ์ฆ่ากันเอง ชาวบ้านก็เดือดร้อน',
      'หยุดเถิด! ที่นี่มีเด็ก!',
    ],
  },
  {
    who: ['guard'],
    on: ANY,
    when: { recent: ['pkNear'] },
    act: 'argue',
    w: 3,
    lines: ['หยุดเดี๋ยวนี้! ห้ามทำร้ายกันในเขตเมือง!', 'ใครลงมืออีก ข้าจะจับเข้าคุก!'],
  },
  {
    who: 'all',
    on: IDLE,
    when: { recent: ['boss'] },
    lines: [
      'ได้ยินไหม ปีศาจตนนั้นออกอาละวาดอีกแล้ว',
      'จอมยุทธ์หลายคนไปปราบปีศาจแล้วไม่กลับมา',
      'ปิดประตูหน้าต่างให้ดี ปีศาจยังไม่ไปไหน',
    ],
  },
  {
    who: ['martial', 'master', 'guard'],
    on: ANY,
    when: { recent: ['boss'] },
    lines: ['ใครปราบปีศาจได้ ชื่อจะดังไปทั่วยุทธภพ', 'ข้าอยากไปช่วยปราบ แต่ฝีมือยังไม่ถึง'],
  },
  {
    who: 'all',
    on: ANY,
    when: { recent: ['bossDown'] },
    act: 'cheer',
    w: 3,
    lines: [
      'ปีศาจถูกปราบแล้ว! คืนนี้ได้นอนหลับเต็มตาเสียที',
      'ฉลองกันเถิด ปีศาจตายแล้ว!',
      'ใครหนอเป็นคนปราบ ข้าอยากคารวะสักครั้ง',
    ],
  },

  // ───────────────────────── rumours of the quest under way ─────────────────────────
  {
    who: 'all',
    on: IDLE,
    when: { quest: ['three-forms'] },
    lines: [
      'คนหนุ่มมาฝึกที่ลานฝึกทุกวัน เสียงไม้กระทบกันดังทั้งเช้า',
      'สามกระบวนท่าแรก ใครๆ ก็ว่าง่าย จนได้ลองเอง',
    ],
  },
  {
    who: 'all',
    on: IDLE,
    when: { quest: ['bandits'] },
    lines: [
      'โจรป่าลงมาปล้นเกวียนเกลืออีกแล้ว',
      'หัวหน้าโจรนั่นใช้ดาบใหญ่ ฟันทีเดียวเกวียนขาด',
      'พ่อค้าไม่กล้าออกเดินทางกลางคืนแล้ว',
    ],
  },
  {
    who: ['guard'],
    on: IDLE,
    when: { quest: ['bandits'] },
    lines: ['ค่ายโจรอยู่บนเนิน ข้ามีคนไม่พอจะบุก', 'ถ้าใครปราบโจรได้ ทางการจะมีรางวัลให้'],
  },
  {
    who: 'all',
    on: IDLE,
    when: { quest: ['wolves'] },
    lines: [
      'เมื่อคืนหมาป่าคาบไก่บ้านป้าไปสองตัว',
      'เสียงหอนจากป่าสนใกล้เข้ามาทุกคืน',
      'ใครจะเข้าป่า ไปเป็นกลุ่มนะ',
    ],
  },
  {
    who: ['farmer', 'child'],
    on: IDLE,
    when: { quest: ['wolves'] },
    act: 'cower',
    lines: ['ข้าเห็นตาหมาป่าวาวๆ ที่ขอบนาเมื่อคืน', 'แม่ไม่ให้ไปเล่นริมป่าแล้ว เพราะหมาป่า'],
  },
  {
    who: 'all',
    on: IDLE,
    when: { quest: ['black-sword'] },
    lines: [
      'แสงแดงขึ้นจากลานเลือดอีกแล้วเมื่อคืน',
      'คนแก่บอกว่ากระบี่ทมิฬไม่ได้หลับ มันแค่รอ',
      'อย่าเดินไปทางใต้ตอนกลางคืนเด็ดขาด',
    ],
  },
  {
    who: ['elder', 'scholar'],
    on: IDLE,
    when: { quest: ['black-sword'] },
    act: 'sigh',
    lines: ['ยี่สิบปีก่อนก็เป็นแบบนี้ แสงแดงก่อน แล้วคนก็หายไป'],
  },
  {
    who: 'all',
    on: IDLE,
    when: { quest: ['monks'] },
    lines: ['พระแปลกหน้าที่วัดร้าง ไม่สวดมนต์ แต่ลับมีดทั้งคืน', 'วัดร้างหลังเขาพันบันไดมีไฟจุดทุกคืน'],
  },
  {
    who: ['monk'],
    on: IDLE,
    when: { quest: ['monks'] },
    act: 'sigh',
    lines: ['ผ้าเหลืองห่มกายได้ แต่ห่มใจไม่ได้', 'คนพวกนั้นไม่ใช่ศิษย์ของพระพุทธองค์'],
  },
  {
    who: 'all',
    on: IDLE,
    when: { quest: ['bears'] },
    lines: ['หมีป่าลงมากินน้ำผึ้งถึงชายป่า', 'พรานเห็นรอยเท้าหมีใหญ่เท่าฝาโอ่ง'],
  },
  {
    who: 'all',
    on: IDLE,
    when: { quest: ['jiangshi'] },
    lines: [
      'ศพที่สุสานใต้ยอดหมอกกระโดดได้!',
      'แปะยันต์ไว้ที่ประตูทุกบ้านแล้ว',
      'ได้ยินเสียงกระดิ่งตอนดึก อย่าเปิดประตู',
    ],
  },
  {
    who: 'all',
    on: IDLE,
    when: { quest: ['shadows'] },
    lines: ['เงาดำวิ่งบนหลังคาเมื่อคืน เร็วเหมือนนก', 'ของหายจากบ้านหลายหลัง ไม่มีใครเห็นขโมยสักคน'],
  },
  {
    who: 'all',
    on: IDLE,
    when: { quest: ['thousand-swords'] },
    lines: [
      'ทางเหนือมีกระบี่ร่วงจากฟ้าเหมือนฝน',
      'ใครปราบจอมมารได้ คงเป็นเถาฉางเยี่ยคนใหม่',
      'ฟ้าทางเหนือเป็นสีเงินทั้งคืน',
    ],
  },

  // ───────────────────────── voices at work, any hour ─────────────────────────
  {
    who: ['vendor'],
    on: IDLE,
    act: 'wave',
    lines: [
      'ผ้าไหมจากเจียงหนาน เนื้อนุ่มเหมือนเมฆ!',
      'ยาสมานแผล ยาแก้พิษ มีครบ!',
      'ซื้อสองแถมหนึ่ง วันนี้วันเดียว!',
      'ชาดอกไม้ร้อนๆ หอมชื่นใจ!',
      'ของดีราคาซื่อ ข้าค้าขายด้วยสัจจะ',
    ],
  },
  {
    who: ['innkeeper'],
    on: IDLE,
    lines: [
      'เสี่ยวเอ้อ! โต๊ะสามรอสุราอยู่!',
      'แขกเยอะแบบนี้ ต้องนึ่งซาลาเปาอีกลังแล้ว',
      'จอมยุทธ์กินดื่มเก่ง แต่จ่ายเงินช้าเหลือเกิน',
    ],
  },
  {
    who: ['farmer'],
    on: IDLE,
    lines: ['ดินดำน้ำชุ่ม ปีนี้คงพอกิน', 'หลังแทบหัก แต่ข้าวสวยก็คุ้ม', 'วัวข้าแก่แล้ว ไถช้าลงทุกปี'],
  },
  {
    who: ['fisher'],
    on: IDLE,
    lines: ['ปลาตัวใหญ่หลุดไปอีกแล้ว!', 'สมัยก่อนปลาชุมกว่านี้มาก', 'อวนขาดอีกแล้ว ต้องนั่งซ่อมทั้งคืน'],
  },
  {
    who: ['guard'],
    on: IDLE,
    lines: [
      'ยืนเวรทั้งวัน ขาแข็งเป็นตอไม้',
      'ทหารกินเบี้ยหลวงน้อยนิด แต่งานไม่เคยน้อย',
      'ทุกคนในเมืองนี้ข้าจำหน้าได้หมด',
    ],
  },
  {
    who: ['child'],
    on: IDLE,
    lines: [
      'ฮึบ! ฮ่า! ท่ากระบี่ไร้เทียมทาน!',
      'ไก่ตัวนั้นวิ่งไวกว่าข้าอีก!',
      'วันหนึ่งข้าจะเหาะข้ามหลังคาได้!',
    ],
  },
  {
    who: ['martial'],
    on: IDLE,
    lines: [
      'กระบวนท่าที่สามยังไม่ลื่นไหล ต้องฝึกอีก',
      'ลมหายใจต้องตามดาบ ไม่ใช่ดาบตามลมหายใจ',
      'วันนี้ต้องชนะให้ได้',
    ],
  },
  {
    who: ['lady'],
    on: IDLE,
    lines: ['ดอกไม้ที่หุบเขาคงบานเต็มแล้ว', 'น้ำชาดี ต้องรินช้าๆ', 'ใครหนอเล่นขลุ่ยอยู่ไกลๆ'],
  },
  {
    who: ['monk'],
    on: IDLE,
    lines: ['อมิตาภพุทธ', 'กวาดใบไม้ก็คือกวาดใจ', 'ระฆังเย็นดังแล้ว ใจก็สงบลง'],
  },
  {
    who: ['scholar'],
    on: IDLE,
    lines: [
      'ปีนี้สอบเข้าเมืองหลวงอีกครั้ง คงไม่พลาดแล้ว',
      'พู่กันข้าทื่อแล้ว เหมือนใจคนเขียน',
      'บทกวีบทนี้ ขาดอีกคำเดียว…',
    ],
  },
  {
    who: ['guide'],
    on: IDLE,
    lines: [
      'หิมะบนยอดเขาละลายช้าปีนี้',
      'รถม้าเที่ยวเช้าออกไปแล้ว เที่ยวหน้ายังมี',
      'ทางเหนือเปิดแล้ว ข้าเพิ่งเดินกลับมา',
    ],
  },
  {
    who: ['master'],
    on: IDLE,
    lines: [
      'ศิษย์รุ่นนี้ยังอ่อนนัก',
      'ขาตั้งให้มั่น อย่าโยกเหมือนต้นหญ้า',
      'สมัยข้าหนุ่มๆ ฝึกตั้งแต่ไก่ยังไม่ขัน',
    ],
  },

  // ───────────────────────── คุณหนู, ไกด์, บัณฑิต: more of their own ─────────────────────────
  {
    who: ['lady'],
    on: GREET,
    when: { part: ['dawn', 'day'] },
    lines: ['แดดเช้าอ่อนโยนนัก เหมาะแก่การเดินชมสวน', 'ท่านจอมยุทธ์ตื่นเช้าเหมือนนกขมิ้นเชียว'],
  },
  {
    who: ['lady'],
    on: ANY,
    when: { weather: ['rain'] },
    lines: ['ฝนโปรยแบบนี้ ชวนให้แต่งกลอนนัก', 'ร่มกระดาษของข้าน้อยเล็กไป ท่านหลบชายคาก่อนเถิด'],
  },
  {
    who: ['lady'],
    on: GREET,
    when: { hurt: true },
    lines: ['ท่านบาดเจ็บ… ข้าน้อยมีผ้าสะอาด ให้พันแผลก่อนเถิด'],
  },
  {
    who: ['lady'],
    on: ANY,
    when: { recent: ['wonDuel'] },
    w: 2,
    lines: ['เพลงกระบี่ของท่าน งามดั่งระบำบุปผา'],
  },
  {
    who: ['lady'],
    on: GREET,
    when: { standing: ['friendly', 'honoured'] },
    act: 'bow',
    w: 2,
    lines: ['คุณชาย{name}มาแล้ว ข้าน้อยชงชารอไว้แล้ว', 'ได้พบท่านอีก ช่างเป็นวาสนา'],
  },
  {
    who: ['lady'],
    on: IDLE,
    lines: [
      'ปิ่นหยกอันนี้ท่านแม่ให้มา ข้าน้อยไม่เคยถอด',
      'ขลุ่ยไผ่เล่าเพลงได้มากกว่าคำพูด',
      'สายลมจากหุบเขาบุปผาพัดมาถึงที่นี่ด้วย',
    ],
  },
  {
    who: ['lady'],
    on: GIFT,
    act: 'bow',
    w: 2,
    lines: ['ข้าน้อยรับไว้ด้วยใจ จะเย็บถุงหอมตอบแทนท่าน', 'ท่านมีน้ำใจนัก ข้าน้อยจะจดจำ'],
  },
  {
    who: ['guide'],
    on: GREET,
    when: { realm: [0, 0] },
    lines: [
      'หน้าใหม่หรือ เดินถนนใหญ่ไว้ก่อน ป่าลึกยังไม่ใช่ที่ของเจ้า',
      'จะไปลานศิลา ขึ้นรถม้าที่ท่ารถม้าสะดวกกว่า',
    ],
  },
  {
    who: ['guide'],
    on: ANY,
    when: { weather: ['rain', 'storm', 'fog'] },
    lines: ['อากาศแบบนี้ ทางเลียบผาลื่นนัก อย่าเพิ่งขึ้นเขา', 'หมอกลงแบบนี้ ข้าเองยังต้องเดินช้า'],
  },
  {
    who: ['guide'],
    on: ANY,
    when: { weather: ['snow'] },
    lines: ['หิมะปิดทางเหนือแล้ว รอสักสองสามวัน'],
  },
  {
    who: ['guide'],
    on: GREET,
    when: { part: ['night'] },
    lines: ['กลางคืนอย่าเดินนอกถนนใหญ่ หมาป่าตาไวกว่าคน'],
  },
  {
    who: ['guide'],
    on: IDLE,
    lines: [
      'ข้ามแม่น้ำต้องดูน้ำขึ้นน้ำลงให้ดี',
      'วิชาตัวเบาช่วยได้มาก ข้ามหลังคาไปเร็วกว่าเดินอ้อมครึ่งวัน',
      'ยอดเขาลูกนั้น ข้าเคยขึ้นไปครั้งเดียว ลมแรงจนแทบปลิว',
    ],
  },
  {
    who: ['guide'],
    on: GREET,
    when: { standing: ['friendly', 'honoured'] },
    w: 2,
    lines: ['สหาย{name} ข้ารู้ทางลัดใหม่ อยากรู้ไหม', 'จะไปไหนก็บอก ข้าพาไปเองไม่คิดเงิน'],
  },
  {
    who: ['guide'],
    on: GIFT,
    act: 'laugh',
    w: 2,
    lines: [
      'ขอบใจ เงินนี้ซื้อรองเท้าใหม่ได้คู่หนึ่ง ของเก่าขาดแล้ว',
      'น้ำใจท่าน ข้าจะตอบแทนด้วยทางที่ปลอดภัยที่สุด',
    ],
  },
  {
    who: ['scholar'],
    on: ANY,
    when: { part: ['night'] },
    lines: ['จุดตะเกียงอ่านตำราอีกสักบท แล้วค่อยนอน', 'แสงเดือนสว่างพอจะอ่านหนังสือได้'],
  },
  {
    who: ['scholar'],
    on: ANY,
    when: { weather: ['rain'] },
    lines: ['ฝนตกพรำๆ เหมาะแก่การเขียนพู่กัน', 'หมึกละลายหมดแล้ว ฝนนี่ไม่เมตตาบัณฑิตเลย'],
  },
  {
    who: ['scholar'],
    on: GREET,
    when: { school: ['pine-ink'] },
    act: 'bow',
    lines: ['ศิษย์หอหมึกสน ตำราของท่านข้าเคยคัดลอกมาแล้ว'],
  },
  {
    who: ['scholar'],
    on: GREET,
    when: { karma: ['evil', 'mad'] },
    act: 'sigh',
    w: 2,
    lines: ['ปราชญ์ว่า คนชั่วไม่อาจหลบเงาตนเองได้'],
  },
  {
    who: ['scholar'],
    on: GREET,
    when: { standing: ['friendly', 'honoured'] },
    act: 'bow',
    w: 2,
    lines: ['ท่าน{name} ข้าแต่งกลอนถึงท่านไว้บทหนึ่ง', 'มิตรผู้รู้ใจหาได้ยากกว่าตำราหายาก'],
  },
  {
    who: ['scholar'],
    on: IDLE,
    lines: [
      'ตัวอักษรนี้เขียนสิบครั้งยังไม่งาม',
      'ประวัติศาสตร์ของเก้ากระบี่ ไม่มีตำราไหนเขียนครบ',
      'หมึกดี กระดาษดี แต่ใจยังไม่นิ่ง',
    ],
  },
  {
    who: ['scholar'],
    on: GIFT,
    act: 'bow',
    w: 2,
    lines: [
      'ขอบคุณ ข้าจะซื้อกระดาษมาเขียนตำราเล่มใหม่',
      'ผู้ให้ตำลึงแก่บัณฑิต เหมือนรดน้ำต้นไม้ที่ยังไม่ออกผล',
    ],
  },

  // ───────────────────────── how they regard the player ─────────────────────────
  {
    who: 'all',
    on: GREET,
    when: { standing: ['honoured'] },
    act: 'bow',
    w: 3,
    lines: [
      'ท่าน {name} ผู้มีพระคุณ! เชิญๆ',
      'ทั้งหมู่บ้านเป็นหนี้บุญคุณท่าน',
      'ท่านมาเยือน เป็นเกียรติแก่พวกเรานัก',
      'ผู้มีพระคุณมาแล้ว! ไปต้มชามาเร็ว',
    ],
  },
  {
    who: ['vendor', 'innkeeper'],
    on: GREET,
    when: { standing: ['honoured'] },
    act: 'bow',
    w: 3,
    lines: ['สำหรับท่าน ข้าลดให้เป็นพิเศษ ไม่ต้องเกรงใจ', 'ของดีที่สุดข้าเก็บไว้ให้ท่านคนเดียว'],
  },
  {
    who: ['child'],
    on: GREET,
    when: { standing: ['honoured', 'friendly'] },
    act: 'wave',
    w: 3,
    lines: ['พี่{name}มาแล้ว! พี่{name}มาแล้ว!', 'ข้าเก็บลูกท้อไว้ให้พี่ด้วยนะ'],
  },
  {
    who: 'all',
    on: GREET,
    when: { standing: ['friendly'] },
    act: 'bow',
    w: 2,
    lines: [
      'อ้าว {name} มาอีกแล้ว ดีใจที่ได้เจอ',
      'วันนี้สบายดีหรือ {name}',
      'เพื่อนเก่ามาเยือน มานั่งคุยกันก่อน',
    ],
  },
  {
    who: ['guard'],
    on: GREET,
    when: { standing: ['friendly', 'honoured'] },
    act: 'baoquan',
    w: 2,
    lines: ['ท่าน {name} ถนนข้างหน้าข้าเพิ่งตรวจมา ปลอดภัย', 'มีท่านอยู่ ข้าอุ่นใจขึ้นเยอะ'],
  },
  {
    who: 'all',
    on: GREET,
    when: { standing: ['wary'] },
    w: 2,
    lines: ['…มีธุระอะไร', 'ข้าไม่อยากมีเรื่องนะท่าน', 'เชิญผ่านไปเถิด', 'ข้าได้ยินเรื่องของท่านมาบ้าง…'],
  },
  {
    who: ['vendor', 'innkeeper'],
    on: GREET,
    when: { standing: ['wary'] },
    w: 2,
    lines: ['จ่ายก่อนแล้วค่อยรับของนะท่าน', 'ร้านข้าไม่อยากมีปัญหา ซื้อแล้วไปเถิด'],
  },
  {
    who: 'all',
    on: GREET,
    when: { standing: ['hated'] },
    act: 'cower',
    w: 4,
    lines: [
      'อย่าเข้ามาใกล้ข้า!',
      'ไปให้พ้น! ที่นี่ไม่ต้อนรับคนอย่างเจ้า',
      'ข้าจำหน้าเจ้าได้… อย่าทำร้ายข้า',
      'ปิดประตู! มันมาอีกแล้ว!',
    ],
  },
  {
    who: ['guard', 'martial', 'master'],
    on: GREET,
    when: { standing: ['hated'] },
    act: 'argue',
    w: 4,
    lines: ['ออกไปจากเมืองนี้ ก่อนข้าจะหมดความอดทน', 'คนอย่างเจ้า ข้าไม่ต้อนรับ'],
  },
  {
    who: ['elder', 'monk'],
    on: GREET,
    when: { standing: ['hated'] },
    act: 'sigh',
    w: 4,
    lines: ['เจ้าทำให้ชาวบ้านทุกข์ใจนัก {name}', 'กลับตัวเสีย ก่อนที่ทุกประตูจะปิดใส่เจ้า'],
  },
  {
    who: 'all',
    on: IDLE,
    when: { standing: ['honoured', 'friendly'] },
    lines: ['คนอย่าง {name} หาได้ยากในยุทธภพ', 'ข้าเล่าเรื่องของ {name} ให้หลานฟังทุกคืน'],
  },
  {
    who: 'all',
    on: IDLE,
    when: { standing: ['hated', 'wary'] },
    lines: ['คนนั้นอีกแล้ว… เก็บของมีค่าให้ดี', 'ชื่อเสียงของ {name} ไม่ใช่ชื่อเสียงดี'],
  },

  // ───────────────────────── a gift ─────────────────────────
  {
    who: 'all',
    on: GIFT,
    act: 'bow',
    lines: ['ท่านใจดีนัก ข้าไม่ลืมแน่', 'ขอบคุณมาก ข้ารับไว้ด้วยความเกรงใจ', 'น้ำใจเช่นนี้ หาได้ยากในยุคนี้'],
  },
  {
    who: ['child'],
    on: GIFT,
    act: 'laugh',
    w: 2,
    lines: ['ให้ข้าจริงหรือ! จะเอาไปซื้อขนมหวาน!', 'ขอบคุณพี่! ข้าจะเก็บไว้อย่างดี'],
  },
  {
    who: ['monk'],
    on: GIFT,
    act: 'bow',
    w: 2,
    lines: ['บุญนี้ อาตมาจะนำไปบูรณะวัด อมิตาภพุทธ'],
  },
  {
    who: 'all',
    on: GIFT,
    when: { standing: ['hated', 'wary'] },
    w: 3,
    lines: ['…ข้ารับไว้ แต่ไม่ใช่ว่าข้าลืมแล้ว', 'เงินซื้อใจคนไม่ได้หรอก… แต่ก็ขอบคุณ'],
  },
];
